import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  IconButton,
  Container,
  Link,
  SimpleGrid,
  Image,
  HStack,
  VStack,
  Badge,
} from "@chakra-ui/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ExternalLinkIcon,
} from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";

const PRIMARY = "#49BFF1";

const slides = [
  {
    title: "Staminexa — Mobile App Studio",
    subtitle: "Creative, reliable and fast mobile solutions.",
    bg: "linear-gradient(135deg, #E8F8FF 0%, #F6FBFF 100%)",
  },
  {
    title: "Beat Voices",
    subtitle: "Creative audio apps focused on music and beat creation.",
    bg: "linear-gradient(135deg, #EAF7FF 0%, #FFFFFF 100%)",
  },
  {
    title: "Culinary AI",
    subtitle: "Generate personalized recipes using artificial intelligence.",
    bg: "linear-gradient(135deg, #F0FBFF 0%, #FFFFFF 100%)",
  },
];

const projects = [
  {
    name: "Beat Voices",
    desc: "Create professional-quality beats using your voice or instruments, loop them and enhance with effects for your creative projects.",
    url: "https://play.google.com/store/apps/details?id=com.staminexa.beatvoices",
    tag: "Music",
    more: "/beat-voices",
    icon: require("../images/app-icons/beat-voices.jpg"),
  },
  {
    name: "Culinary AI",
    desc: "Discover delicious recipes tailored to your preferences and dietary needs with an AI-powered recipe generator.",
    url: "https://play.google.com/store/apps/details?id=com.aesthetic.culinary",
    tag: "AI / Food",
    icon: require("../images/app-icons/culinary-ai.jpg"),
    more: "/culinary-ai",
  },
  {
    name: "Ingreveal",
    desc: "Scan and analyze product ingredients with AI-powered insights, detect allergens and additives instantly.",
    url: "https://play.google.com/store/apps/details?id=com.staminexa.ingreveal",
    tag: "Tools",
    icon: require("../images/app-icons/ingreveal.jpg"),
  },
  {
    name: "Limpid - Mobile Teleprompter",
    desc: "Professional teleprompter for creators with script management, recording and adjustable scroll speed.",
    url: "https://play.google.com/store/apps/details?id=com.aesthetic.limpid",
    tag: "Tools",
    icon: require("../images/app-icons/limpid.jpg"),
  },
  {
    name: "PomoBoost",
    desc: "Pomodoro timer with customizable work/rest intervals, alarms and focus features to boost productivity.",
    url: "https://play.google.com/store/apps/details?id=com.aesthetic.pomodoro",
    tag: "Productivity",
    icon: require("../images/app-icons/pomo-boost.jpg"),
  },
];

function Header() {
  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={30}
      bg="white"
      boxShadow="sm"
      backdropFilter="saturate(180%) blur(6px)"
    >
      <Container maxW="container.xl">
        <Flex py={3} align="center" justify="space-between">
          <HStack spacing={4} align="center">
            <Heading size="md" color={PRIMARY} letterSpacing="tight">
              Staminexa
            </Heading>
            <HStack
              as="nav"
              spacing={4}
              display={{ base: "none", md: "flex" }}
              align="center"
            >
              <Link href="#home">Home</Link>
              <Link href="#about">About</Link>
              <Link href="#projects">Projects</Link>
              <Link href="#contact">Contact</Link>
            </HStack>
          </HStack>
          <HStack spacing={2}>
            <Button
              as={Link}
              href="#projects"
              size="sm"
              bg={PRIMARY}
              color="white"
              _hover={{ bg: "#2da7d9" }}
            >
              Our Apps
            </Button>
            {false ? (
              <Button variant="ghost" size="sm">
                Sign In
              </Button>
            ) : null}
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}

function Slider() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      5000,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <Box as="section" id="home">
      <Box bg={slides[index].bg} py={{ base: 12, md: 24 }}>
        <Container maxW="container.xl">
          <Flex
            direction={{ base: "column", md: "row" }}
            align="center"
            gap={8}
          >
            <Box flex={1}>
              <Badge colorScheme="blue" mb={4} bg={PRIMARY} color="white">
                Featured
              </Badge>
              <Heading mb={4}>{slides[index].title}</Heading>
              <Text fontSize="lg" mb={6} color="gray.600">
                {slides[index].subtitle}
              </Text>
              <HStack>
                <Button
                  bg={PRIMARY}
                  color="white"
                  _hover={{ bg: "#2da7d9" }}
                  as={Link}
                  href="#projects"
                >
                  Explore Projects
                </Button>
                <Button
                  variant="outline"
                  as={Link}
                  href="mailto:contactoixobt@gmail.com"
                >
                  Contact Us
                </Button>
              </HStack>
            </Box>

            <Box flex={1} textAlign="center">
              <Image
                alt="illustration"
                src={require("../images/slider-image.jpg")}
                borderRadius="md"
                mx="auto"
                maxH="320px"
                objectFit="cover"
                shadow="md"
              />
            </Box>
          </Flex>
          <Flex mt={6} justify="center" gap={2} align="center">
            <IconButton
              aria-label="prev"
              icon={<ArrowLeftIcon />}
              size="sm"
              onClick={() =>
                setIndex((i) => (i - 1 + slides.length) % slides.length)
              }
            />
            <HStack spacing={2}>
              {slides.map((s, i) => (
                <Box
                  key={i}
                  w={3}
                  h={3}
                  bg={i === index ? PRIMARY : "gray.300"}
                  borderRadius="full"
                  cursor="pointer"
                  onClick={() => setIndex(i)}
                />
              ))}
            </HStack>
            <IconButton
              aria-label="next"
              icon={<ArrowRightIcon />}
              size="sm"
              onClick={() => setIndex((i) => (i + 1) % slides.length)}
            />
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}

function About() {
  const aboutEN1 =
    "Staminexa develops mobile utility applications that simplify everyday tasks and creative workflows. Our goal is to deliver reliable, easy-to-use, and innovative solutions that both boost user productivity and enable new experiences.";

  const aboutEN2 =
    "Our products leverage AI and modern mobile technologies across education, entertainment and productivity domains. We follow a user-centered design approach, prioritize data privacy, and continuously iterate to improve our offerings.";

  return (
    <Box as="section" id="about" py={{ base: 10, md: 16 }}>
      <Container maxW="container.lg">
        <Box
          bg="white"
          p={{ base: 6, md: 10 }}
          borderRadius="md"
          boxShadow="sm"
        >
          <Heading size="md">About</Heading>
          <Text color="gray.600" fontSize={{ base: "md", md: "lg" }}>
            {aboutEN1}
          </Text>
          <Text color="gray.600" fontSize={{ base: "md", md: "lg" }}>
            {aboutEN2}
          </Text>
        </Box>
      </Container>
    </Box>
  );
}

function Projects() {
  return (
    <Box as="section" id="projects" py={{ base: 12, md: 20 }}>
      <Container maxW="container.xl">
        <VStack align="start" spacing={4} mb={6}>
          <Heading>Our Projects</Heading>
          <Text color="gray.600">
            A selection of mobile applications developed by Staminexa. Lorem
            ipsum dolor sit amet.
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {projects.map((p) => (
            <Box
              key={p.name}
              p={6}
              borderWidth={1}
              borderRadius="md"
              boxShadow="sm"
              bg="white"
            >
              <Flex align="start" gap={4}>
                {p.icon ? (
                  <Image
                    boxSize="64px"
                    src={p.icon}
                    alt={`${p.name} icon`}
                    borderRadius="md"
                    objectFit="cover"
                  />
                ) : (
                  <Box
                    boxSize="64px"
                    bg={PRIMARY}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="white"
                    fontWeight="bold"
                  >
                    {p.name
                      .split(" ")
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join("")}
                  </Box>
                )}
                <Box flex={1}>
                  <Flex justify="space-between" align="start">
                    <Box>
                      <Heading size="md">{p.name}</Heading>
                      <Text mt={2} color="gray.600">
                        {p.desc}
                      </Text>
                    </Box>
                    <Badge colorScheme="green">{p.tag}</Badge>
                  </Flex>

                  <HStack mt={4} spacing={3}>
                    <Button
                      as={Link}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      leftIcon={<ExternalLinkIcon />}
                      colorScheme="blue"
                    >
                      View on Play
                    </Button>
                    <RouterLink to={p.more} target="_blank">
                      <Button variant="ghost" size="sm">
                        Learn More
                      </Button>
                    </RouterLink>
                  </HStack>
                </Box>
              </Flex>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}

function Footer() {
  return (
    <Box as="footer" bg="#0f1724" color="white" py={8} mt={12}>
      <Container maxW="container.xl">
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align="center"
        >
          <VStack align="start">
            <Heading size="sm">Staminexa</Heading>
            <Text fontSize="sm" color="gray.300">
              © {new Date().getFullYear()} Staminexa. All rights reserved.
            </Text>
          </VStack>
          <HStack spacing={4} mt={{ base: 4, md: 0 }}>
            <Link
              color="gray.300"
              href="https://www.oixobt.space/culinary-ai/privacy-policy"
            >
              Privacy
            </Link>
            <Link
              color="gray.300"
              href="https://www.oixobt.space/culinary-ai/terms-of-use"
            >
              Terms
            </Link>
            <Link color="gray.300" href="#contact">
              Contact
            </Link>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}

const Home = () => {
  return (
    <Box bg="gray.50" minH="100vh">
      <Header />
      <Slider />
      <About />
      <Projects />
      <Box as="section" id="contact" py={{ base: 10, md: 16 }}>
        <Container maxW="container.md">
          <VStack spacing={4} textAlign="center">
            <Heading>Get in touch</Heading>
            <Text color="gray.600">
              Have questions or want a demo? Get in touch and we'll reply
              shortly.
            </Text>
            <HStack>
              <Button
                as={Link}
                href="mailto:contactoixobt@gmail.com"
                bg={PRIMARY}
                color="white"
                _hover={{ bg: "#2da7d9" }}
              >
                Contact Us
              </Button>
              <Button variant="outline" display="none">
                Request Demo
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default Home;
